import React, {useContext, useEffect, useRef, useState} from "react";
import {
    DataGrid,
    DataGridDataRowProps,
    DataGridHeaderRowProps,
    IconAndText,
    Modal,
    RadioButtonGroup,
    SORT_ORDER,
    SortElementProps,
    Text
} from "atmosfair-ui"
import UsersForm, {UserFormProps, UsersFormProps} from "../../../forms/users-form/UsersForm";
import {useTranslation} from "react-i18next";
import {
    allInternationalPhonePrefixesFormattedForDataGridAlpha2CountryCode,
    getPossibleAgenciesToMoveUserTo
} from "../../utils";
import {AgencyProps, SortAgencies, WindowedAgencyProps} from "../../../../api/model/agency";
import ToastWithTextOnly from "../../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {toast} from "react-toastify";
import {
    useChangeUsersAgencyMutation,
    useInviteUsersMutation,
    useLazyGetAllAgenciesForKlimaLinkAdminQuery,
    useMakeUserToAgencyAdminMutation,
    useRemoveUserFromAgencyMutation,
    useResendInvitationEmailMutation,
    useUnmakeUserFromAgencyAdminMutation,
    useUpdateUserInfoAdminMutation
} from "../../../../redux/reducer/api";
import {PersonalUserInformation, UserProps} from "../../../../api/model/user";
import {theme} from "../../../../styles/theme";
import {CompanyAgencyAddress, GENERAL_BACKEND_RESPONSE, ScrollDirection} from "../../../../api/model/common";
import {cloneDeep, isEqual} from "lodash";
import SectionTitle from "../../section-title/SectionTitle";
import {v4 as uuidv4} from "uuid";
import {useAppSelector} from "../../../../redux/reducer/store";
import {QueryStatus} from "@reduxjs/toolkit/query";
import {VALIDATE_FORM_STARTING_NUMBER} from "../../../../constants/default-values";
import {isAgencyProps, isWindowedAgencyProps} from "../../../../utils/check/type-guards";
import SpinnerForKlimaLinkAdmin from "../../common/SpinnerForKlimaLinkAdmin";
import AgenciesCompanyDataGrid from "../agencies-company-data-grid/AgenciesCompanyDataGrid";
import SearchInput from "../../../../molecules/search-input/SearchInput";
import ErrorText from "../../../../molecules/errors/error-text/ErrorText";
import {InstanceContext} from "../../../protected-layout/ProtectedLayout";
import {isSomeAdmin} from "../../../../utils/check/user";
import {CompanyProps} from "../../../../api/model/company";
import {getFullName} from "../../../../utils/getter/common";

interface AgencyMembersDataGridProps {
    agencies: UsersFormProps["agencies"],
    allUsers: UserProps[],
    userData: UserProps,
    isKlimaLinkAdmin: boolean,
    companies: CompanyProps[],
}

const DEFAULT_SORT_VARIABLE = SortAgencies.agencyName
const DEFAULT_SORT_ORDER = SORT_ORDER.ASC


interface ModalProps {
    editUser: {
        formData: UserFormProps,
        originalUserObject: OriginalUserObjectProps,
        agencyAdmin: boolean,
        modalVisible: boolean,
    },
    deleteUser: {
        user: RawMemberAgencyData | null,
        modalVisible: boolean,
    }
    addUser: {
        formData: UserFormProps,
        modalVisible: boolean,
    },
    makeUnmakeAdmin: {
        user: UserProps | null,
        agency: AgencyProps | WindowedAgencyProps | null
        agencyAdmin: boolean,
        modalVisible: boolean,
    }
    changeUserAgency: {
        user: UserProps | null,
        agency: AgencyProps | WindowedAgencyProps | null,
        agencyAdmin: boolean,
        modalVisible: boolean,
        newAgencyIndex: number,
        newAgency: AgencyProps | WindowedAgencyProps | null
    }
}

const DEFAULT_FORM_DATA = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    isAdmin: false,
    internationalPhonePrefix: "",
    agency: null,
}

const DEFAULT_MODAL_DATA: ModalProps = {
    addUser: {
        formData: DEFAULT_FORM_DATA,
        modalVisible: false,
    },
    editUser: {
        formData: DEFAULT_FORM_DATA,
        originalUserObject: {} as OriginalUserObjectProps,
        agencyAdmin: false,
        modalVisible: false,
    },
    deleteUser: {
        user: null,
        modalVisible: false,
    },
    makeUnmakeAdmin: {
        user: null,
        agency: null,
        agencyAdmin: false,
        modalVisible: false,
    },
    changeUserAgency: {
        user: null,
        agency: null,
        agencyAdmin: false,
        modalVisible: false,
        newAgencyIndex: 0,
        newAgency: null
    }
}

interface RawMemberAgencyData {
    user: UserProps,
    agency: AgencyProps,
}

interface OriginalUserObjectProps extends Omit<PersonalUserInformation, 'internationalPhonePrefix'> {
    isAdmin: boolean,
    internationalPhonePrefix: CompanyAgencyAddress["internationalPhonePrefix"] | null,
}

interface FormProps extends OriginalUserObjectProps {
    agency: AgencyProps | WindowedAgencyProps | null
}


const AgencyMembersDataGrid = ({
                                   userData,
                                   agencies,
                                   allUsers,
                                   isKlimaLinkAdmin,
                                   companies,
                               }: AgencyMembersDataGridProps) => {
    const [formData, setFormData] = useState<UserFormProps>({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        isAdmin: false,
        internationalPhonePrefix: "",
        agency: null,
    })
    const [sortVariable, setSortVariable] = useState<SortAgencies>(DEFAULT_SORT_VARIABLE)
    const [sortingOrder, setSortingOrder] = useState<SortElementProps["active"]>(DEFAULT_SORT_ORDER)
    const [isLoadingAgencies, setIsLoadingAgencies] = useState(isKlimaLinkAdmin)
    const [headers, setHeaders] = useState<DataGridHeaderRowProps["headers"]>([])
    const [rows, setRows] = useState<DataGridDataRowProps["rows"]>([])
    const [membersCount, setMembersCount] = useState<number | null>(null)
    const [validateForm, setValidateForm] = useState(VALIDATE_FORM_STARTING_NUMBER)
    const [modalProps, setModalProps] = useState<ModalProps>(DEFAULT_MODAL_DATA)
    const userResendInvitationEmail = useRef<UserProps | null>(null)
    const nextScrollPosition = useRef<string | null>(null)
    const isDataFinished = useRef<boolean>(false)
    const searchTerm = useRef<string | null>(null)
    const {t, i18n} = useTranslation()
    const {resolvedLanguage} = i18n
    const {disableButtonOnNotProduction} = useContext(InstanceContext);
    const {isCompanyAdmin: userIsCompanyAdmin,} = isSomeAdmin(userData, agencies, companies)
    const [getAllAgenciesForKlimaLinkAdmin, {
        isError: isErrorGetAllAgenciesForKlimaLinkAdmin,
    }] = useLazyGetAllAgenciesForKlimaLinkAdminQuery()
    const [removeUserFromAgency, {
        isLoading: isLoadingRemoveUserFromAgency,
    }] = useRemoveUserFromAgencyMutation()
    const [inviteUser, {
        data: inviteUserResponse,
        isLoading: isLoadingInviteUser,
    }] = useInviteUsersMutation()
    const [updateUserInfo, {
        data: updateUserInfoResponse,
        isLoading: isLoadingUpdateUserInfo,
    }] = useUpdateUserInfoAdminMutation()
    const [makeUserToAgencyAdmin, {
        isLoading: isLoadingMakeUserAdminToAgencyAdmin,
    }] = useMakeUserToAgencyAdminMutation()
    const [unmakeUserFromAgencyAdmin, {
        isLoading: isLoadingUnmakeUserFromAgencyAdmin,
    }] = useUnmakeUserFromAgencyAdminMutation()
    const [changeUsersAgency, {
        isLoading: isLoadingChangeUsersAgency,
        data: changeUsersAgencyResponse,
    }] = useChangeUsersAgencyMutation()
    const [resendInvitationEmail, {
        isLoading: isLoadingResendInvitationEmail,
        isSuccess: isSuccessResendInvitationEmail,
        isError: isErrorResendInvitationEmail,
    }] = useResendInvitationEmailMutation()
    const userAlreadyExistToastShown = useRef<string[]>([])

    useEffect(() => {
        const fetchData = async () => {
            _resetOnFetch()
            if (isKlimaLinkAdmin) {
                const rows = await _fetchRows()
                setIsLoadingAgencies(false)
                setRows(rows)
            } else {
                setRows(_toUsersDataGrid(agencies));
            }
        }

        fetchData().catch((error) => {
            console.error("Error fetching data:", error);
        });
    }, [isKlimaLinkAdmin, sortingOrder, sortVariable, resolvedLanguage, agencies])

    const _setModalProps = <K extends keyof ModalProps>(key: K, payload: ModalProps[K]) => setModalProps({
        ...modalProps,
        [key]: payload
    })

    const isLoadingGetUsersAgency = useAppSelector(state => {
        return Object.values(state.api.queries).some(query => {
            if (query && query.endpointName === "getUsersAgencies") return query.status === QueryStatus.pending;
        });
    });

    const isErrorGetUsersAgency = useAppSelector(state => {
        return Object.values(state.api.queries).some(query => {
            if (query && query.endpointName === "getUsersAgencies") return query.status === QueryStatus.rejected;
        });
    });

    const _updateFormData = <K extends keyof FormProps>(key: K, payload: FormProps[K]) => setFormData({
        ...formData,
        [key]: payload
    })

    useEffect(() => {
        if (isSuccessResendInvitationEmail && userResendInvitationEmail.current !== null) {
            toast(<ToastWithTextOnly
                    message={t("TOASTS.SUCCESS.RESEND_INVITATION_EMAIL",
                        {email: userResendInvitationEmail.current.email}
                    )}/>,
                {progress: undefined, type: "success"}
            );
            userResendInvitationEmail.current = null
        }
    }, [isSuccessResendInvitationEmail]);

    useEffect(() => {
        if (isErrorResendInvitationEmail && userResendInvitationEmail.current !== null) {
            toast(<ToastWithTextOnly
                    message={t("TOASTS.ERROR.RESEND_INVITATION_EMAIL",
                        {email: userResendInvitationEmail.current.email}
                    )}/>,
                {progress: undefined, type: "error"}
            );
            userResendInvitationEmail.current = null
        }
    }, [isErrorResendInvitationEmail]);

    useEffect(() => {
        if (agencies.length === 1) _updateFormData('agency', agencies[0])
    }, [agencies]);

    useEffect(() => {
        if (isErrorGetUsersAgency) toast(<ToastWithTextOnly
                message={"ERRORS.GENERAL_REFETCHING"}/>,
            {progress: undefined, type: "error"}
        );
    }, [isErrorGetUsersAgency]);

    useEffect(() => {
        if (inviteUserResponse?.status === GENERAL_BACKEND_RESPONSE.success) _resetDialogue()
    }, [inviteUserResponse])

    useEffect(() => {
        if (updateUserInfoResponse?.status === GENERAL_BACKEND_RESPONSE.success) _resetDialogue()
    }, [updateUserInfoResponse])

    useEffect(() => {
        setHeaders(_setHeaders())
    }, [sortingOrder, sortVariable])

    useEffect(() => {
        if (changeUsersAgencyResponse?.status === GENERAL_BACKEND_RESPONSE.success && isKlimaLinkAdmin) {
            setIsLoadingAgencies(true)
            const fetchData = async () => {
                _resetOnFetch()
                const rows = await _fetchRows()
                setIsLoadingAgencies(false)
                setRows(rows)
            }
            // not sure why a time out is needed
            // if timeout is not added, the agencies are fetched with updated data (user is in new agency)
            // but the new data is not reflected in the <DataGrid />
            setTimeout(() => {
                fetchData().catch((error) => {
                    console.error("Error fetching data:", error);
                })
            }, 2500)
        }
    }, [changeUsersAgencyResponse]);

    const _resetOnFetch = () => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        setRows([])
        setMembersCount(null)
    }

    const _setHeaders = () => {
        if (isKlimaLinkAdmin) return [
            {text: "GENERAL.NAME"},
            {text: "GENERAL.EMAIL"},
            {text: "GENERAL.PHONE"},
            {
                text: "GENERAL.AGENCY",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortAgencies.agencyName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortAgencies.agencyName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetch()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortAgencies.agencyName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortAgencies.agencyName);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetch()
                        }
                    },
                    disabled: false,
                },
            },
            {
                text: "GENERAL.COMPANY",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortAgencies.companyName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortAgencies.companyName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetch()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortAgencies.companyName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortAgencies.companyName);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetch()
                        }
                    },
                    disabled: false,
                },
            },
            {text: "GENERAL.ADMIN"}
        ]

        return [
            {text: "GENERAL.NAME"},
            {text: "GENERAL.EMAIL"},
            {text: "GENERAL.PHONE"},
            {text: "GENERAL.ADMIN"}
        ]
    }

    const _fetchRows = async (): Promise<DataGridDataRowProps["rows"]> => {
        let newRows: DataGridDataRowProps["rows"] = []
        if (!isDataFinished.current) {
            try {
                const data = await getAllAgenciesForKlimaLinkAdmin({
                    scrollPosition: nextScrollPosition.current,
                    size: 10, // returns 10 agencies with n members per agency
                    direction: ScrollDirection.forward,
                    sort: sortVariable,
                    sortOrder: sortingOrder,
                    searchTerm: searchTerm.current,
                }).unwrap();
                nextScrollPosition.current = (data?.nextPosition ?? null);
                if (data?.nextPosition === null || data?.nextPosition === "null") isDataFinished.current = true
                newRows = _toUsersDataGrid(data.agencies)
                setMembersCount(data.membersCount)
                if (isDataFinished.current && searchTerm === null) {
                    const updatedRows = cloneDeep(rows) as DataGridDataRowProps["rows"]
                    newRows.forEach(newRow => updatedRows.push(newRow))
                    newRows = updatedRows
                }
            } catch (error) {
                console.warn("Something went wrong getting agencies: ", error)
            }
        } else {
            return newRows
        }
        return newRows
    }

    const _generateDisableAndTooltipDatagridValues = (
        isAgencyAdmin: boolean,
        registrationComplete: boolean,
        deactivated: boolean,
        email: string,
    ) => {
        let disableEdit, disableDelete, disableChangeUsersAgency, disableMakeUnmakeUserAdmin,
            disableResendInvitationEmail = false
        let editTooltip, deleteTooltip, changeUsersAgencyTooltip, makeUnmakeUserAdminTooltip,
            resendInvitationEmailTooltip = ''
        if (disableButtonOnNotProduction || deactivated) {
            disableEdit = true
            disableDelete = true
            disableChangeUsersAgency = true
            disableMakeUnmakeUserAdmin = true
            disableResendInvitationEmail = true
            if (deactivated) {
                const disableUserDeactivatedTooltip = "TOOLTIP.SETTINGS.USER_DEACTIVATED"
                editTooltip = disableUserDeactivatedTooltip
                deleteTooltip = disableUserDeactivatedTooltip
                changeUsersAgencyTooltip = disableUserDeactivatedTooltip
                makeUnmakeUserAdminTooltip = disableUserDeactivatedTooltip
                resendInvitationEmailTooltip = disableUserDeactivatedTooltip
            } else {
                const disableButtonOnNotProductionTooltip = "TOOLTIP.COMMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE"
                editTooltip = disableButtonOnNotProductionTooltip
                deleteTooltip = disableButtonOnNotProductionTooltip
                changeUsersAgencyTooltip = disableButtonOnNotProductionTooltip
                makeUnmakeUserAdminTooltip = disableButtonOnNotProductionTooltip
                resendInvitationEmailTooltip = disableButtonOnNotProductionTooltip
            }
        } else {
            if (isAgencyAdmin) {
                disableEdit = true
                disableDelete = true
                disableChangeUsersAgency = true
                disableMakeUnmakeUserAdmin = true
                editTooltip = "TOOLTIP.SETTINGS.EDIT_OTHER_ADMIN"
                deleteTooltip = "TOOLTIP.SETTINGS.DELETE_OTHER_ADMIN"
                changeUsersAgencyTooltip = 'TOOLTIP.SETTINGS.CHANGE_AGENCY_OTHER_ADMIN'
                makeUnmakeUserAdminTooltip = "TOOLTIP.SETTINGS.UNMAKE_OTHER_AGENCY_ADMIN"
                if (userIsCompanyAdmin) {
                    disableEdit = false
                    disableDelete = false
                    disableMakeUnmakeUserAdmin = false
                    editTooltip = "TOOLTIP.SETTINGS.EDIT_USER"
                    deleteTooltip = "TOOLTIP.SETTINGS.DELETE_USER"
                    makeUnmakeUserAdminTooltip = "TOOLTIP.SETTINGS.REMOVE_ADMIN"
                    changeUsersAgencyTooltip = "TOOLTIP.SETTINGS.CHANGE_AGENCY_IS_AGENCY_ADMIN"
                }
            } else {
                disableEdit = false
                disableDelete = false
                disableChangeUsersAgency = false
                disableMakeUnmakeUserAdmin = false
                editTooltip = "TOOLTIP.SETTINGS.EDIT_USER"
                deleteTooltip = "TOOLTIP.SETTINGS.DELETE_USER"
                makeUnmakeUserAdminTooltip = "TOOLTIP.SETTINGS.MAKE_ADMIN"
                changeUsersAgencyTooltip = "TOOLTIP.SETTINGS.CHANGE_USERS_AGENCY"
            }

            if (registrationComplete) {
                disableResendInvitationEmail = true
                resendInvitationEmailTooltip = "TOOLTIP.SETTINGS.RESEND_INVITATION_EMAIL_ALREADY_REGISTERED"
            } else {
                resendInvitationEmailTooltip = t("TOOLTIP.SETTINGS.RESEND_INVITATION_EMAIL", {
                    email,
                    interpolation: {escapeValue: false}
                })
            }

        }

        return {
            disableEdit,
            disableDelete,
            disableChangeUsersAgency,
            disableMakeUnmakeUserAdmin,
            disableResendInvitationEmail,
            editTooltip,
            deleteTooltip,
            changeUsersAgencyTooltip,
            makeUnmakeUserAdminTooltip,
            resendInvitationEmailTooltip
        }
    }

    const _toUsersDataGrid = (agencies: AgencyProps[] | WindowedAgencyProps[]) => {
        const usersDataGridRows: DataGridDataRowProps["rows"] = []
        agencies.forEach(agency => {
            let members: UserProps[]
            let companyName = ''
            if (isWindowedAgencyProps(agency)) {
                members = agency.members
                companyName = agency.companyName
            } else {
                members = allUsers.filter(expedient => expedient.agencyIds.includes(agency.id))
            }
            const {name, id: agencyId} = agency
            members.forEach(expedient => {
                const {
                    firstName,
                    lastName,
                    email,
                    internationalPhonePrefix,
                    phoneNumber,
                    id,
                    registrationComplete,
                    deactivated,
                } = expedient
                const isSameUser = userData.id === id
                let memberIsAgencyAdmin = false
                if (isAgencyProps(agency)) {
                    memberIsAgencyAdmin = agency.adminIds.includes(id)
                } else {
                    memberIsAgencyAdmin = agency.admins.map(admin => admin.id).includes(id)
                }

                const {
                    disableEdit,
                    disableDelete,
                    disableChangeUsersAgency,
                    disableMakeUnmakeUserAdmin,
                    disableResendInvitationEmail,
                    editTooltip,
                    deleteTooltip,
                    changeUsersAgencyTooltip,
                    makeUnmakeUserAdminTooltip,
                    resendInvitationEmailTooltip
                } = _generateDisableAndTooltipDatagridValues(
                    memberIsAgencyAdmin,
                    registrationComplete,
                    deactivated,
                    email
                )

                const isAdminString = memberIsAgencyAdmin ? "GENERAL.YES" : "GENERAL.NO"
                const fullName = `${firstName} ${lastName}${!registrationComplete ? "*" : ""}`
                let completePhoneNumber
                if (allInternationalPhonePrefixesFormattedForDataGridAlpha2CountryCode[internationalPhonePrefix] && phoneNumber.length > 0) {
                    completePhoneNumber = `${allInternationalPhonePrefixesFormattedForDataGridAlpha2CountryCode[internationalPhonePrefix]} ${phoneNumber}`
                } else {
                    completePhoneNumber = null
                }

                usersDataGridRows.push({
                    data: [
                        {
                            text: fullName,
                            value: fullName,
                        },
                        {
                            text: email,
                            value: email
                        },
                        {
                            text: completePhoneNumber ?? " -- ",
                            value: completePhoneNumber ? phoneNumber : 0
                        },
                        ...(isKlimaLinkAdmin
                            ? [
                                {
                                    text: name,
                                    value: name,
                                },
                                {
                                    text: companyName,
                                    value: companyName,
                                },
                            ]
                            : []),
                        {
                            text: isAdminString,
                            value: isAdminString,
                        }
                    ],
                    group: name,
                    key: uuidv4(),
                    disabled: deactivated,
                    tooltip: deactivated ? "TOOLTIP.SETTINGS.USER_DEACTIVATED" : undefined,
                    actions: [
                        {
                            hide: isSameUser,
                            disabled: disableEdit,
                            icon: "penToSquare",
                            tooltip: editTooltip,
                            onClick: _onEditRow
                        },
                        {
                            hide: isSameUser,
                            disabled: disableDelete,
                            icon: "trash",
                            tooltip: deleteTooltip,
                            onClick: (_, userAgency) => {
                                _setModalProps("deleteUser", {
                                    user: userAgency,
                                    modalVisible: true,
                                })
                                // userFromAgencyToDelete.current = userAgency
                                // userIsAgencyAdmin.current = userAgency.agency.isAdmin
                                // _setModalsVisible("deleteUserModalVisible", true)
                            }
                        },
                        {
                            hide: isSameUser,
                            disabled: disableMakeUnmakeUserAdmin,
                            icon: memberIsAgencyAdmin ? "userTie" : "crown",
                            tooltip: makeUnmakeUserAdminTooltip,
                            onClick: () => {
                                _setModalProps("makeUnmakeAdmin", {
                                    user: expedient,
                                    agency,
                                    agencyAdmin: memberIsAgencyAdmin,
                                    modalVisible: true,
                                })
                            }
                        },
                        {
                            hide: isSameUser,
                            icon: "repeat",
                            disabled: disableChangeUsersAgency,
                            tooltip: changeUsersAgencyTooltip,
                            onClick: () => {
                                _setModalProps("changeUserAgency", {
                                    ...modalProps.changeUserAgency,
                                    user: expedient,
                                    agency,
                                    agencyAdmin: memberIsAgencyAdmin,
                                    modalVisible: true,
                                })
                            }
                        },
                        {
                            hide: isSameUser,
                            disabled: disableResendInvitationEmail,
                            icon: "envelope",
                            tooltip: resendInvitationEmailTooltip,
                            onClick: () => {
                                userResendInvitationEmail.current = expedient
                                resendInvitationEmail({email: expedient.email, agencyId})
                            }
                        },
                    ],
                    rawData: {
                        user: expedient,
                        agency
                    } as RawMemberAgencyData,
                })
            })
        })

        return usersDataGridRows
    }

    const _inviteUser = (data: UserFormProps) => {
        const {email} = data
        if (email && allUsers.some(user => user.email === email) && !userAlreadyExistToastShown.current.find(user => user === email)) {
            toast(<ToastWithTextOnly
                    message={t("SCREENS.SETTINGS.ADD_USER_EMAIL_ALREADY_IN_USE",
                        {email}
                    )}/>,
                {progress: undefined, type: "error"}
            );
            // ugly workaround
            // when an Email with an existing user was entered, and then a non existing email is entered, the toast informing
            // the user that the user with the email already exist will still be shown and then, the user is correctly invited
            // why? Becaasue users form gatheres in an useEffect() validateForm AND the updated user data, and because of thats,
            // its triggered twice. First with the old email (because of validateForm) and then with the new value (user data)
            userAlreadyExistToastShown.current.push(email)
        } else {
            const {
                firstName,
                lastName,
                email,
                isAdmin,
                agency
            } = data
            if (email && agency) {
                inviteUser({
                    users: [{
                        firstName,
                        lastName,
                        email,
                        isAdmin: Boolean(isAdmin),
                        language: resolvedLanguage ?? "de",
                    }],
                    agencyId: agency.id
                })
            }
        }
    }

    const _deleteUser = (user: RawMemberAgencyData | null) => {
        if (user) {
            removeUserFromAgency({
                agencyId: user.agency.id,
                userEmail: user.user.email
            })
            _resetDialogue()
        }
    }

    const _editUser = (data: UserFormProps, originalUserObject: OriginalUserObjectProps) => {
        const {
            firstName,
            lastName,
            email,
            phoneNumber,
            internationalPhonePrefix,
        } = data
        const updatedUserObject = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phoneNumber: phoneNumber.trim(),
            internationalPhonePrefix: internationalPhonePrefix!!,
        }

        if (!isEqual(updatedUserObject, originalUserObject) && email) {
            updateUserInfo({
                userObject: updatedUserObject,
                userEmail: email
            })
        }
    }

    const _onEditRow = (_: number, userAgency: RawMemberAgencyData) => {
        const {agency, user} = userAgency
        const {firstName, lastName, email, phoneNumber, internationalPhonePrefix} = user
        const {isAdmin} = agency
        _setModalProps("editUser", {
            formData: {
                firstName,
                lastName,
                phoneNumber,
                isAdmin,
                internationalPhonePrefix,
                email,
                agency
            },
            originalUserObject: {
                firstName,
                lastName,
                email,
                isAdmin,
                phoneNumber,
                internationalPhonePrefix,
            },
            agencyAdmin: isAdmin,
            modalVisible: true,
        })
        setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
    }

    const _makeUnmakeAdmin = (user: UserProps | null, agency: AgencyProps | WindowedAgencyProps | null, isAdmin: boolean) => {
        if (user && agency) {
            const {email} = user
            const agencyId = agency.id
            if (!isAdmin) makeUserToAgencyAdmin({userEmail: email, agencyId})
            if (isAdmin) unmakeUserFromAgencyAdmin({userEmail: email, agencyId})
        }
        _resetDialogue()
    }

    const _onConfirmChangeUsersAgency = (props: ModalProps["changeUserAgency"], newAgency: AgencyProps | WindowedAgencyProps | null) => {
        const {agency, user, newAgencyIndex} = props
        if (agency && user) {
            if (!isKlimaLinkAdmin) {
                const possibleAgenciesToMoveUserTo = getPossibleAgenciesToMoveUserTo(agencies, user)
                changeUsersAgency({
                    oldAgencyId: agency.id,
                    newAgencyId: agencies.find(agency => agency.id === possibleAgenciesToMoveUserTo[newAgencyIndex].id)?.id!!,
                    userEmail: user.email
                })
            } else {
                if (newAgency !== null) changeUsersAgency({
                    oldAgencyId: agency.id,
                    newAgencyId: newAgency.id,
                    userEmail: user.email
                })
            }
        }
        _resetDialogue()
    }

    const _onChange = async (query: string) => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        const queryForSearch = query.length > 0 ? query : null
        searchTerm.current = queryForSearch
        const newRows = await _fetchRows()
        setRows(newRows)
    }

    const _renderChangeUsersAgency = (props: ModalProps["changeUserAgency"]) => {
        const {user, agency, agencyAdmin, newAgencyIndex} = props
        const Headline = <Text style={isKlimaLinkAdmin ? {marginBottom: theme.spacer.large} : {}}>
            {`${t("MODALS.SETTINGS.CHANGE_USERS_AGENCY_TEXT", {
                name: getFullName(user),
                oldAgency: agency?.name,
                interpolation: {escapeValue: false}
            })}`}
        </Text>

        if (isKlimaLinkAdmin) return <>
            {Headline}
            <AgenciesCompanyDataGrid
                agenciesToHide={user?.agencyIds}
                allUsers={allUsers}
                isCompanyAdmin={userIsCompanyAdmin}
                whichType={"agencies"}
                displayInSelectAgencyModal={true}
                onSelectAgency={agency => _onConfirmChangeUsersAgency(props, agency)}
                isKlimaLinkAdmin={isKlimaLinkAdmin}/>
            <div style={{height: '20px'}}/>
        </>

        return <>
            {Headline}
            {agencyAdmin &&
                <Text>{`${t("SCREENS.SETTINGS.CHANGE_USERS_AGENCY_ADMIN_TEXT", {
                    name: getFullName(user),
                    interpolation: {escapeValue: false}
                })}`}</Text>}
            <RadioButtonGroup
                fieldsetLabel={"LABELS.SETTINGS.CHOOSE_USERS_AGENCY"}
                containerStyle={{marginTop: theme.spacer.large}}
                onChange={index => _setModalProps("changeUserAgency", {
                    ...modalProps.changeUserAgency,
                    newAgencyIndex: index,
                })
                }
                radioButtons={getPossibleAgenciesToMoveUserTo(agencies, user)}
                selected={newAgencyIndex}
                name={"SCREENS.TRAVEL_COMPONENTS_OVERVIEW.RADIO_BUTTON_GROUP"}
                orientation={"vertical"}
                labelStyle={{fontSize: theme.font.fontSize.regular}}
                hideLabel={false}/>
        </>
    }

    const _renderDataGrid = () => {
        if (isErrorGetAllAgenciesForKlimaLinkAdmin) <ErrorText>ERRORS.GENERAL_NETWORK_ERROR_BODY</ErrorText>
        if (isLoadingAgencies) return <SpinnerForKlimaLinkAdmin/>
        return <DataGrid
            look={"primaryLight"}
            cellWeights={headers.map(_ => 1)}
            headers={headers}
            rows={rows}
            rowSize={"medium"}
            groupRows={!isKlimaLinkAdmin}
            {...(isKlimaLinkAdmin ? {
                fetchRows: () => _fetchRows()
            } : {})}
        />

    }

    const _getAgencyOfAdminForInviteUserModal = (): AgencyProps | null => {
        const {agencyIds} = userData
        if (agencyIds.length === 1) {
            const adminsAgency = agencies.find(agency => agencyIds.includes(agency.id))
            if (adminsAgency) return adminsAgency
            return null
        }
        return null
    }

    const _renderAddButton = () => {
        if (agencies.length > 0) return <>
            {!isKlimaLinkAdmin && <div style={{width: '200px'}}>
                <IconAndText
                    disabled={disableButtonOnNotProduction}
                    tooltip={disableButtonOnNotProduction ? "TOOLTIP.SETTINGS.INVITE_USERS_ON_STAGING" : undefined}
                    iconColor={theme.colors.primary}
                    text={"SCREENS.SETTINGS.ADD_NEW_USER"}
                    onClick={() => _setModalProps("addUser", {
                        formData: {
                            ...modalProps.addUser.formData,
                            agency: _getAgencyOfAdminForInviteUserModal(),
                        },
                        modalVisible: true,
                    })}
                    icon={"plus"}/>
            </div>}
            {_renderDataGrid()}
        </>
        return <Text>SCREENS.SETTINGS.NO_AGENCY_HINT_ADD_USERS</Text>
    }

    const _resetDialogue = () => {
        setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
        setModalProps(DEFAULT_MODAL_DATA)
    }

    const _renderEditAddUserModal = () => {
        const {addUser, editUser} = modalProps
        const {
            modalVisible: addUserModalVisible,
            formData: addUserFormData,
        } = addUser

        const {
            modalVisible: editUserModalVisible,
            formData: editUserFormData,
            originalUserObject,
        } = editUser

        return <Modal
            headline={`SCREENS.SETTINGS.${editUserModalVisible ? "EDIT_USER" : "ADD_NEW_USER"}`}
            show={addUserModalVisible || editUserModalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.OK"}
            onConfirm={() => setValidateForm(validateForm + 1)}
            onBackdropClick={_resetDialogue}
            onClose={_resetDialogue}
            showSpinner={isLoadingInviteUser || isLoadingUpdateUserInfo}
            showPaper={true}>
            <UsersForm
                onEnterKeyPressed={() => setValidateForm(validateForm + 1)}
                defaultValues={addUserModalVisible ? addUserFormData : editUserFormData}
                showAgencySelection={editUserModalVisible ? false : agencies.length > 1}
                showEmailField={addUserModalVisible}
                showPhoneNumberFields={editUserModalVisible}
                showIsAdminCheckbox={true}
                validateForm={validateForm}
                onFormIsValid={data => {
                    if (addUserModalVisible) _inviteUser(data)
                    if (editUserModalVisible) _editUser(data, originalUserObject)
                }}
                agencies={agencies}/>
        </Modal>
    }

    const _renderMakeUnmakeAdminModal = () => {
        const {
            makeUnmakeAdmin: {
                user,
                agency,
                agencyAdmin,
                modalVisible,
            }
        } = modalProps

        return <Modal
            headline={t(
                `MODALS.SETTINGS.${agencyAdmin ? "UNMAKE_ADMIN_TITLE" : "MAKE_ADMIN_TITLE"}`,
                {user: getFullName(user), interpolation: {escapeValue: false}}
            )}
            showSpinner={isLoadingMakeUserAdminToAgencyAdmin || isLoadingUnmakeUserFromAgencyAdmin || isLoadingResendInvitationEmail}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.OK"}
            onEnterKeyPressed={() => _makeUnmakeAdmin(user, agency, agencyAdmin)}
            onConfirm={() => _makeUnmakeAdmin(user, agency, agencyAdmin)}
            onBackdropClick={_resetDialogue}
            onClose={_resetDialogue}
            showPaper={true}>
            <Text>{t(
                `MODALS.SETTINGS.${agencyAdmin ? "UNMAKE_USER_ADMIN_TEXT" : "MAKE_USER_ADMIN_TEXT"}`,
                {user: getFullName(user), interpolation: {escapeValue: false}}
            )}</Text>
        </Modal>
    }

    const _renderChangeUsersAgencyModal = () => {
        const {changeUserAgency} = modalProps
        const {modalVisible, user, agency} = changeUserAgency
        return <Modal
            headline={t(`MODALS.SETTINGS.CHANGE_USERS_AGENCY_TITLE`, {
                name: getFullName(user),
                interpolation: {escapeValue: false}
            })}
            showSpinner={isLoadingChangeUsersAgency}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.OK"}
            onConfirm={() => _onConfirmChangeUsersAgency(changeUserAgency, agency)}
            onEnterKeyPressed={() => _onConfirmChangeUsersAgency(changeUserAgency, agency)}
            onBackdropClick={_resetDialogue}
            onClose={_resetDialogue}
            showPaper={true}>
            {_renderChangeUsersAgency(changeUserAgency)}
        </Modal>
    }

    const _renderDeleteUserModal = () => {
        const {
            deleteUser: {
                modalVisible,
                user,
            }
        } = modalProps
        return <Modal
            headline={t("SCREENS.SETTINGS.DELETE_USER", {
                user: getFullName(user?.user ?? null),
                interpolation: {escapeValue: false}
            })}
            onEnterKeyPressed={() => _deleteUser(user)}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.DELETE"}
            confirmButtonColor={"warningMain"}
            onConfirm={() => _deleteUser(user)}
            onBackdropClick={_resetDialogue}
            onClose={_resetDialogue}
            showSpinner={isLoadingRemoveUserFromAgency}
            showPaper={true}>
            <Text>{t("SCREENS.SETTINGS.DELETE_USER_TEXT", {
                user: getFullName(user?.user ?? null),
                interpolation: {escapeValue: false}
            })}</Text>
        </Modal>
    }

    return <section>
        <SectionTitle
            isLoading={
                isLoadingRemoveUserFromAgency ||
                isLoadingInviteUser ||
                isLoadingUpdateUserInfo ||
                isLoadingMakeUserAdminToAgencyAdmin ||
                isLoadingUnmakeUserFromAgencyAdmin ||
                isLoadingChangeUsersAgency ||
                isLoadingGetUsersAgency
            }
            subTitle={isKlimaLinkAdmin && membersCount !== null ? `${t("GENERAL.TOTAL")}: ${membersCount} ${t("GENERAL.EXPEDIENTS")}` : undefined}
            title={"GENERAL.EXPEDIENTS_AGENCIES"}/>
        <div className={"agencies-company-wrapper"}>
            {isKlimaLinkAdmin && <div style={{width: '100%'}}>
                <SearchInput
                    label={"SCREENS.SETTINGS.SEARCH_BAR_LABEL_MEMBERS_AGENCIES"}
                    placeholder={"GENERAL.SEARCH"}
                    onChange={_onChange}
                />
            </div>}
            {_renderAddButton()}
        </div>
        {_renderEditAddUserModal()}
        {_renderDeleteUserModal()}
        {_renderMakeUnmakeAdminModal()}
        {_renderChangeUsersAgencyModal()}
    </section>
}

export default AgencyMembersDataGrid